/**
 * Media type detection utilities.
 * Determines if a file or extension belongs to a specific media category.
 *
 * @module media-type-detector
 */

import { ToTypes } from '../../types/types';
import {
    IMAGE_EXTENSIONS,
    AUDIO_EXTENSIONS,
    VIDEO_EXTENSIONS,
} from './media-extensions';

// Pre-compute lowercase sets for O(1) lookup
const IMAGE_SET = new Set(IMAGE_EXTENSIONS.map(ext => ext.toLowerCase()));
const AUDIO_SET = new Set(AUDIO_EXTENSIONS.map(ext => ext.toLowerCase()));
const VIDEO_SET = new Set(VIDEO_EXTENSIONS.map(ext => ext.toLowerCase()));

/**
 * Normalize extension for comparison (lowercase, no leading dot)
 */
function normalizeExtension(ext: string): string {
    return ext.toLowerCase().replace(/^\./, '');
}

// ============================================================================
// TO TYPE DETECTION (Output format)
// ============================================================================

/**
 * Check if the target type is a video format
 * @param toType - The target output type or extension
 */
export function isVideoToType(toType: string): boolean {
    const normalized = normalizeExtension(toType);
    return normalized === ToTypes.VIDEO.toLowerCase() || VIDEO_SET.has(normalized);
}

/**
 * Check if the target type is an audio format
 * @param toType - The target output type or extension
 */
export function isAudioToType(toType: string): boolean {
    const normalized = normalizeExtension(toType);
    return normalized === ToTypes.AUDIO.toLowerCase() || AUDIO_SET.has(normalized);
}

/**
 * Check if the target type is an image format
 * @param toType - The target output type or extension
 */
export function isImageToType(toType: string): boolean {
    const normalized = normalizeExtension(toType);
    return normalized === ToTypes.IMAGE.toLowerCase() || IMAGE_SET.has(normalized);
}

// ============================================================================
// FROM TYPE DETECTION (Input format)
// ============================================================================

/**
 * Check if any of the source types is a video format
 * @param fromTypes - Array of source types/extensions
 */
export function isVideoFromType(fromTypes: string[]): boolean {
    return fromTypes.some(fromType => {
        const normalized = normalizeExtension(fromType);
        return normalized === ToTypes.VIDEO.toLowerCase() || VIDEO_SET.has(normalized);
    });
}

/**
 * Check if any of the source types is an audio format
 * @param fromTypes - Array of source types/extensions
 */
export function isAudioFromType(fromTypes: string[]): boolean {
    return fromTypes.some(fromType => {
        const normalized = normalizeExtension(fromType);
        return normalized === ToTypes.AUDIO.toLowerCase() || AUDIO_SET.has(normalized);
    });
}

/**
 * Check if any of the source types is an image format
 * @param fromTypes - Array of source types/extensions
 */
export function isImageFromType(fromTypes: string[]): boolean {
    return fromTypes.some(fromType => {
        const normalized = normalizeExtension(fromType);
        return normalized === ToTypes.IMAGE.toLowerCase() || IMAGE_SET.has(normalized);
    });
}

// ============================================================================
// EXTENSION DETECTION
// ============================================================================

/**
 * Detect the media type of an extension
 * @param extension - The file extension to check
 * @returns 'video' | 'audio' | 'image' | 'unknown'
 */
export function detectMediaType(extension: string): 'video' | 'audio' | 'image' | 'unknown' {
    const normalized = normalizeExtension(extension);

    if (VIDEO_SET.has(normalized)) return 'video';
    if (AUDIO_SET.has(normalized)) return 'audio';
    if (IMAGE_SET.has(normalized)) return 'image';

    return 'unknown';
}

/**
 * Check if an extension is a video format
 * @param extension - The file extension to check
 */
export function isVideoExtension(extension: string): boolean {
    return VIDEO_SET.has(normalizeExtension(extension));
}

/**
 * Check if an extension is an audio format
 * @param extension - The file extension to check
 */
export function isAudioExtension(extension: string): boolean {
    return AUDIO_SET.has(normalizeExtension(extension));
}

/**
 * Check if an extension is an image format
 * @param extension - The file extension to check
 */
export function isImageExtension(extension: string): boolean {
    return IMAGE_SET.has(normalizeExtension(extension));
}
